import { useState, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import { api } from '../services/api';
import RewriteModal from '../components/RewriteModal';

interface IntentVolumeResult {
  analysis: string;
  source_detected: string;
  total_keywords: number;
  keywords_analyzed: number;
  truncated: boolean;
  input_tokens: number;
  output_tokens: number;
}

export default function IntentVolumePage() {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<IntentVolumeResult | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [copied, setCopied] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [showRewriteModal, setShowRewriteModal] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ALLOWED_EXTENSIONS = ['.csv', '.xlsx', '.xls'];

  const validateFile = (f: File): boolean => {
    const ext = '.' + f.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setError(`Unsupported file type. Accepted: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return false;
    }
    return true;
  };

  const handleFileSelect = (f: File) => {
    setError(null);
    if (validateFile(f)) {
      setFile(f);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFileSelect(dropped);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleAnalyze = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResult(null);
      setEditedContent('');

      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post('/intentvolume/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 minutes
      });

      setResult(res.data);
      setEditedContent(res.data.analysis);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(editedContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleTextSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const ta = e.currentTarget;
    setSelectedText(ta.value.substring(ta.selectionStart, ta.selectionEnd));
  };

  const handleRewriteAccept = (rewrittenText: string) => {
    setEditedContent(editedContent.replace(selectedText, rewrittenText));
    setShowRewriteModal(false);
    setSelectedText('');
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setEditedContent('');
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Intent Volume Checker</h1>
          <p className="text-gray-500 mt-1">
            Upload keyword exports from Google Search Console, Ahrefs, or Semrush to group keywords by user intent, classify funnel stages (TOFU/MOFU/BOFU), and aggregate metrics.
          </p>
        </div>

        {/* Upload Section */}
        {!result && (
          <div className="bg-white rounded-xl border shadow-sm p-6 mb-6">
            <div className="space-y-4">
              {/* Drop Zone */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  dragOver
                    ? 'border-brand-500 bg-brand-50'
                    : file
                    ? 'border-green-300 bg-green-50'
                    : 'border-gray-300 hover:border-brand-400 hover:bg-gray-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFileSelect(f);
                  }}
                  className="hidden"
                />

                {file ? (
                  <div className="space-y-2">
                    <svg className="w-10 h-10 mx-auto text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                    <p className="text-xs text-brand-600">Click or drop to replace</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <svg className="w-10 h-10 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="text-sm font-medium text-gray-700">
                      Drag & drop your keyword export here
                    </p>
                    <p className="text-xs text-gray-500">
                      or click to browse — CSV, XLSX, XLS supported
                    </p>
                    <p className="text-xs text-gray-400">
                      Works with Google Search Console, Ahrefs, and Semrush exports
                    </p>
                  </div>
                )}
              </div>

              {/* Analyze Button */}
              <button
                onClick={handleAnalyze}
                disabled={loading || !file}
                className="w-full py-3 rounded-lg bg-brand-600 text-white font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Analyzing keywords... (this may take 30-60 seconds)
                  </>
                ) : (
                  'Analyze Keywords'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {/* Toolbar */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
              <div>
                <h3 className="font-medium text-gray-900">
                  Intent Analysis
                  <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">
                    {result.source_detected}
                  </span>
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {result.keywords_analyzed.toLocaleString()} keywords analyzed
                  {result.truncated && (
                    <span className="text-amber-600 ml-1">
                      (truncated from {result.total_keywords.toLocaleString()})
                    </span>
                  )}
                  {' '}&middot;{' '}
                  Tokens: {result.input_tokens?.toLocaleString()} in / {result.output_tokens?.toLocaleString()} out
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleReset}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  New Analysis
                </button>
                <button
                  onClick={() => setShowRewriteModal(true)}
                  disabled={!selectedText}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-purple-300 text-purple-700 hover:bg-purple-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Rewrite
                </button>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  {copied ? (
                    <>
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-green-600">Copied!</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Truncation Warning */}
            {result.truncated && (
              <div className="mx-6 mt-4 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-700 text-sm">
                  Dataset was truncated from {result.total_keywords.toLocaleString()} to {result.keywords_analyzed.toLocaleString()} keywords to fit within processing limits.
                  The analysis covers the first {result.keywords_analyzed.toLocaleString()} rows.
                </p>
              </div>
            )}

            {/* Content */}
            <div className="p-6">
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                onSelect={handleTextSelect}
                className="w-full min-h-[600px] rounded-lg border border-gray-300 px-4 py-3 text-sm font-mono leading-relaxed focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-y"
                spellCheck={false}
              />
            </div>
          </div>
        )}
      </div>

      <RewriteModal
        isOpen={showRewriteModal}
        selectedText={selectedText}
        onClose={() => { setShowRewriteModal(false); setSelectedText(''); }}
        onAccept={handleRewriteAccept}
      />
    </Layout>
  );
}
